import React, { useEffect, useMemo, useState } from 'react';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import throttle from 'lodash/throttle';
import { useTranslation } from 'react-i18next';
import { AutocompleteOption } from './AutoComplete';

export type AutoCompleteMultiSearchProps<T extends AutocompleteOption> = {
  label: string;
  placeholder: string;
  value: T[];
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: T[]) => void;
  collectOptions: (input: string) => Promise<T[]>;
};

const AutoCompleteMultiSearch = <T extends AutocompleteOption>({
  label,
  placeholder,
  value,
  disabled = false,
  loading = false,
  onChange,
  collectOptions,
}: AutoCompleteMultiSearchProps<T>) => {
  const { t } = useTranslation(['random']);
  const theme = useTheme();
  const [options, setOptions] = useState<T[]>([]);
  const [inputValue, setInputValue] = useState('');

  const fetchOptions = useMemo(
    () =>
      throttle((searchText: string, callback: (results: T[]) => void) => {
        collectOptions(searchText).then(callback).catch(console.error);
      }, 500),
    [collectOptions],
  );

  useEffect(() => {
    if (disabled || inputValue.trim() === '') {
      setOptions([]);
      return undefined;
    }

    fetchOptions(inputValue, (results) => {
      setOptions(results);
    });
  }, [disabled, inputValue, fetchOptions]);

  return (
    <Autocomplete
      multiple
      fullWidth
      size="small"
      disabled={disabled}
      loading={loading}
      loadingText={t('loadingText')}
      noOptionsText={t('noOptionsText')}
      options={options}
      value={value}
      filterOptions={(searchResults) => searchResults}
      getOptionLabel={(option: T) => option.name}
      isOptionEqualToValue={(left: T, right: T) => left.id === right.id}
      sx={{
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.blue[60],
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: theme.palette.blue[60],
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.blue[60],
        },
      }}
      onInputChange={(_event: React.SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      onChange={(_event: React.SyntheticEvent, newValue: T[]) => {
        onChange(newValue);
      }}
      renderTags={(selected: T[], getTagProps) =>
        selected.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} variant="outlined" label={option.name} {...tagProps} />;
        })
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label} placeholder={placeholder} />
      )}
    />
  );
};

export { AutoCompleteMultiSearch };
export default AutoCompleteMultiSearch;
